import { NodeWithMaterial, SelectionState } from '../components/NodeBranch';

export function updateChildNodesSelection(
  node: NodeWithMaterial,
  shouldCheck: boolean,
  selection: Record<string, SelectionState>,
) {
  const setSelectionState = ({ id }: { id: string }) => {
    if (shouldCheck) {
      selection[id] = 'checked';
    } else {
      delete selection[id];
    }
  };

  node.material?.forEach(setSelectionState);

  return selection;
}

export function updateRowSelection(
  rowId: string,
  selection: Record<string, SelectionState>,
): Record<string, SelectionState> {
  const rowSelection = selection[rowId];
  if (!rowSelection || rowSelection === 'indeterminate') return { ...selection, [rowId]: 'checked' };
  if (rowSelection === 'checked') {
    const { [rowId]: deleteRow, ...newSelection } = selection;
    return newSelection;
  }

  return selection;
}

export function updateNodeBranchSelection(
  rowId: string,
  node: NodeWithMaterial | undefined,
  selection: Record<string, SelectionState>,
): Record<string, SelectionState> {
  if (!node) return updateRowSelection(rowId, selection);

  const state = selection[rowId];
  let shouldCheckMaterial: boolean = true;

  if (state === 'checked') {
    shouldCheckMaterial = false;
  } else if (state === 'indeterminate') {
    const allDirectSelected = node.material?.every(m => selection[m.id] === 'checked');
    shouldCheckMaterial = !allDirectSelected;
  }

  return updateChildNodesSelection(node, shouldCheckMaterial, selection);
}

export function updateNodeHierarchySelection(
  nodeId: string,
  selection: Record<string, SelectionState>,
  allNodesById: Record<string, NodeWithMaterial>,
) {
  let newSelection = { ...selection };
  const currentNode = allNodesById[nodeId];
  const allMaterialSelected = currentNode.material?.every(material => newSelection[material.id] === 'checked');
  const allChildNodesSelected = currentNode.nodes?.every(child => newSelection[child.id] === 'checked');

  if (allMaterialSelected && allChildNodesSelected) {
    newSelection[currentNode.id] = 'checked';
  } else {
    const allMaterialNotSelected = currentNode.material?.every(material => !newSelection[material.id]);
    const allChildNodesNotSelected = currentNode.nodes?.every(child => !newSelection[child.id]);

    if (allMaterialNotSelected && allChildNodesNotSelected) {
      delete newSelection[currentNode.id];
    } else {
      newSelection[currentNode.id] = 'indeterminate';
    }
  }

  const parentNodeId = currentNode.path?.[currentNode.path.length - 1];
  if (parentNodeId) {
    return updateNodeHierarchySelection(parentNodeId, newSelection, allNodesById);
  }

  return newSelection;
}

export function updateNodeTreeSelection(
  nodeId: string,
  rowId: string,
  selection: Record<string, SelectionState>,
  allNodesById: Record<string, NodeWithMaterial>,
  selectableNodesById: Record<string, NodeWithMaterial> = allNodesById,
) {
  const isBranch = nodeId === rowId;
  const currentNode = selectableNodesById[nodeId] || allNodesById[nodeId];
  const updatedSelection = isBranch
    ? updateNodeBranchSelection(rowId, currentNode, selection)
    : updateRowSelection(rowId, selection);
  return updateNodeHierarchySelection(nodeId, updatedSelection, allNodesById);
}
